/* eslint-disable @typescript-eslint/no-explicit-any */
import { EImportSectionsTarget } from '@constants';
import { ISection } from '@sdk/contracts';
import { Collapse, Flex } from 'antd';
import { isEmpty } from 'lodash';
import { ReactNode, useState } from 'react';
import { useTheme } from 'styled-components';

import { PendingReviewDot } from './PendingReviewDot';
import { SectionDescription } from './SectionDescription';
import { Wrapper } from './Sections.styles';

import { Activities } from '@/components/Activities';
import { ActivityPreview } from '@/components/ActivityPreview';
import { ContentSearchEmptyPage } from '@/components/ContentSearchEmptyPage';
import { Icon } from '@/components/Icon';
import { SectionActions } from '@/components/SectionActions';
import { SectionName } from '@/components/SectionName';
import { SectionsAndIntroductionEmptyPage } from '@/components/SectionsAndIntroductionEmptyPage';
import { SortableProvider } from '@/components/SortableProvider';
import { Box } from '@/components/Utils';
import { useLocalCourse, useProfile } from '@/store';

type Props = {
  sections: ISection[];
};

export const Sections: React.FC<Props> = ({ sections }) => {
  const [openSectionIds, setOpenSectionIds] = useState<string[]>([]);
  const { course, reorderCourseSections } = useLocalCourse();
  const {
    roleCheck: { isManager, isTeacher }
  } = useProfile();
  const { colors } = useTheme();
  const target = EImportSectionsTarget.SECTIONS;
  if (!course) return null;

  const allSections = course.sections || [];
  const hasActiveFilter = allSections.length !== sections.length;

  const handleReorder = (items: ReactNode[]) => {
    const newSectionsOrder = items.map(item => (item as any).props._id);
    reorderCourseSections({ courseId: course._id, newSectionsOrder });
  };

  return (
    <Wrapper>
      {isEmpty(sections) && !hasActiveFilter && (
        <SectionsAndIntroductionEmptyPage target={target} fetchOnlyParentsCourses />
      )}
      {isEmpty(sections) && hasActiveFilter && <ContentSearchEmptyPage />}
      <SortableProvider
        zIndex={10}
        droppableId="sections"
        onReorder={handleReorder}
        isDragDisabled={openSectionIds.length > 0 || !isManager || !!hasActiveFilter}>
        {sections.map(section => (
          <Collapse
            key={section._id}
            {...section}
            activeKey={openSectionIds.includes(section._id) ? [section._id] : []}
            style={{
              marginBottom: 16,
              borderRadius: 8,
              backgroundColor: section.active ? colors.black1 : colors.black2
            }}
            onChange={() => {
              setOpenSectionIds(prev =>
                prev.includes(section._id)
                  ? prev.filter(id => id !== section._id)
                  : [...prev, section._id]
              );
            }}
            expandIcon={({ isActive }) => (
              <Box gap={10} align="center" justifyItems="center" marginTop={10}>
                <Icon i={isActive ? 'ChevronDown' : 'ChevronRight'} size={24} />
                <PendingReviewDot section={section} />
              </Box>
            )}
            items={[
              {
                key: section._id,
                label: (
                  <Flex vertical gap={8}>
                    <SectionName section={section} />
                    <SectionDescription
                      section={section}
                      isSectionSelected={openSectionIds.includes(section._id)}
                    />
                  </Flex>
                ),
                extra: (isManager || isTeacher) && (
                  <SectionActions courseId={course._id} section={section} target={target} />
                ),
                children: <Activities section={section} fetchOnlyParentsCourses />
              }
            ]}
          />
        ))}
      </SortableProvider>
      <ActivityPreview />
    </Wrapper>
  );
};
